import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { updateUserData } from '@store/auth/auth.actions';
import { selectIsAuthenticated, selectUser } from '@store/auth/auth.slice';
import { Form, Formik } from 'formik';
import tw from 'twin.macro';
import Alert from '@components/elements/Alert';
import InputsIndex from '@components/elements/Form/InputsIndex';
import Modal from '@components/elements/Modal/Modal';
import INPUT_IDS from '@constants/inputs/userDataInputs';
import {
  selectModuleConfigClientPanel,
  useAppConfigSelector,
} from '@hooks/useAppConfigSelectors';
import showToast from '@utils/showToast';

import ModalFooterWithButtons from '../Modal/ModalFooterWithButtons';
import { initialValues, useValidationSchema } from './formikData';
import TermsAndConsentsFields from './TermsAndConsentsFields';
import useFields from './useFields';

const MissingDataModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { lastName, firstName, phone, email, serviceTerms } =
    useSelector(selectUser);
  const phoneNumber = typeof phone === 'string' ? phone : phone?.number;
  const { demandPhoneNumber } = useAppConfigSelector(
    selectModuleConfigClientPanel
  );
  const [isOpen, setIsOpen] = useState(false);

  const needServiceTerms = serviceTerms === false;

  const fields = useFields({
    phoneNumber,
    firstName,
    lastName,
    email,
    demandPhoneNumber,
  });

  const validationSchema = useValidationSchema({
    demandPhoneNumber,
    phoneNumber,
    firstName,
    lastName,
    email,
    needServiceTerms,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setIsOpen(false);

      return;
    }

    if (!phoneNumber || !lastName || !firstName || !email || needServiceTerms) {
      setIsOpen(true);
    }
  }, [
    isAuthenticated,
    phoneNumber,
    lastName,
    firstName,
    email,
    needServiceTerms,
  ]);

  const handleSubmit = (values, { setSubmitting, setStatus }) => {
    if (phoneNumber) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.PHONE];
    }
    if (lastName) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.LAST_NAME];
    }
    if (firstName) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.FIRST_NAME];
    }
    if (email) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.EMAIL];
    }
    if (!needServiceTerms) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.SERVICE_AGREEMENT];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.EMAIL_CONSENT];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.SMS_CONSENT];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- FIXME
      delete values[INPUT_IDS.PHONE_CONSENT];
    }

    dispatch(updateUserData(values))
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(unwrapResult)
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .then(() => {
        setIsOpen(false);
        showToast(
          t(
            '$*notification.personalData.success',
            'Dane zostały pomyślnie zaktualizowane!'
          ),
          { type: 'success' }
        );
      })
      // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks -- FIXME
      .catch(error => {
        setStatus({
          apiErrorMessage: error?.message ?? error?.['hydra:description'],
        });
      })
      // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const isCloseDisabled =
    fields.some(({ required }) => required) || needServiceTerms;

  return (
    <Modal
      isOpen={isOpen}
      disableClose={isCloseDisabled}
      onClose={handleClose}
      styles={{
        wrapper: [
          tw`flex w-full max-w-[594px] flex-col overflow-hidden md:(max-h-[90vh] rounded-lg)`,
          needServiceTerms
            ? tw`h-full max-h-full rounded-none md:h-[765px]`
            : tw`md:h-auto`,
        ],
      }}
      desktopSize="lg"
    >
      <Modal.Header>{t('$*components.missingDataModal.title')}</Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form css={tw`flex flex-1 flex-col min-h-0`}>
            <Modal.Content
              cssExtend={tw`flex-1 overflow-y-auto min-h-0 mt-5 mx-6 mb-0`}
            >
              <div tw="grid gap-y-6">
                {fields.length > 0 && (
                  <div
                    css={[
                      tw`grid gap-y-5`,
                      needServiceTerms ? tw`mb-0` : tw`mb-6`,
                    ]}
                  >
                    {fields.map(
                      ({ id, type, label, required, autoComplete }) => (
                        <InputsIndex
                          key={id}
                          id={id}
                          type={type}
                          label={label}
                          required={required}
                          autoComplete={autoComplete}
                          formGroupProps={{
                            styles: {
                              css: tw`mb-0 px-1 md:px-2`,
                              mode: 'replace',
                            },
                          }}
                        />
                      )
                    )}
                  </div>
                )}
                {needServiceTerms && <TermsAndConsentsFields />}
              </div>

              {status?.apiErrorMessage && (
                <Alert styles={{ css: tw`mt-4` }}>
                  {/* i18next-extract-disable-next-line */}
                  {t(`$*error.api.${status.apiErrorMessage}`)}
                </Alert>
              )}
            </Modal.Content>
            <ModalFooterWithButtons
              declineOnClick={handleClose}
              declineText={!isCloseDisabled && t('$*common.skip')}
              acceptText={t('$*common.save')}
              acceptTypeSubmit={true}
              isLoading={isSubmitting}
              fixed={true}
              acceptStyles={tw`gap-2 rounded-[4px] px-[24px] py-[12px] sm:(flex-none min-w-[160px] w-auto)`}
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

MissingDataModal.displayName = 'MissingDataModal';

export default MissingDataModal;
